import CompleteIcon from 'assets/ver2/icons/complete-icon.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import ReceiptDetails from 'components/fx/components/FormTransactionDetails/ReceiptDetails';
import {
  CURRENCY_FLAG_REFERENCES,
  CURRENCY_NAME_REFERENCE,
  DESKTOP_MIN_WIDTH,
  METHOD_DEPOSIT,
  METHOD_EPAYMENT,
  METHOD_PAYID,
  MOBILE_MIN_WIDTH,
  PAYMENT_METHOD_TEXT_REFERENCE,
  TABLET_MAX_WIDTH,
} from 'constants';
import {
  FX_CALCULATOR_FORM_BUY_NAME,
  FX_CALCULATOR_FORM_PAY_NAME,
} from 'constants/fx';
import { format } from 'helpers';
import useFxAddTransaction from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useTheme from 'hooks/useTheme';
import { isEmpty } from 'lodash';
import { Fragment, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import { paragraphMixin } from 'styles';
import DenominationSelector from '../DenominationSelector';

export const FORM_TRANSACTION_DETAILS_MODAL = 'form-transaction-details-modal';

const FormTransactionDetails = ({ isOpen, onClose, isReceipt = false }) => {
  const { t } = useLang();
  const { isHHMTTheme } = useTheme();
  const { isDesktop } = useDetectDevice();

  const {
    currencyOrders,
    pickupLocationSelected,
    pickupDateSelected,
    pickupLocationType,
    orderNote,
    setOrderNote,
    denominations,
    addDenomination,
    cusNotes,
    transactionDetails,
  } = useFxFormAddTransactionStore();
  const { getPickupLocationName, isMakePayment } = useFxAddTransaction();

  const { customer, createdDate, paymentMethod, creditCardNo, orderRef } =
    transactionDetails || {};

  const [denomination, ...noteParts] = orderNote.split('.');

  const formatOrderNote = (orderNote) => {
    return orderNote.split(',').map((item) => {
      const [currency, note] = item.trim().split(' - ');
      return `${currency} - ${note}`;
    });
  };

  useEffect(() => {
    if (!isMakePayment) {
      const denomNote = Object.entries(denominations)
        .map(([key, value]) => `${key} - ${value}`)
        .join(', ');
      setOrderNote(denomNote + '. ' + cusNotes);
    }
  }, [denominations, isMakePayment]);

  const renderHeader = () => {
    if (isReceipt)
      return (
        <HeaderReceipt>
          <img src={CompleteIcon} width={150} height={150} />
          <HeaderReceiptOrderRefText>
            {t('label_order_ref', {
              orderRef,
            })}
          </HeaderReceiptOrderRefText>
          <HeaderReceiptOrderSuccessText>
            {t('label_was_paid')}
          </HeaderReceiptOrderSuccessText>
        </HeaderReceipt>
      );
    return <HeaderText>{t('label_order_detail')}</HeaderText>;
  };

  const renderCurrencyItem = (order) => {
    if (isEmpty(order)) return null;
    return (
      <CurrencyItemWrap isMakePayment={isMakePayment}>
        <GridItem isMakePayment={isMakePayment}>
          {' '}
          <FlagIcon
            style={{ position: 'unset' }}
            src={
              CURRENCY_FLAG_REFERENCES[
                order?.currentCurrencySelected?.currencyCode
              ]
            }
          />
        </GridItem>
        <GridItem isMakePayment={isMakePayment}>
          <CurrencyCodeBuyAmount>
            <CurrencyChip>
              {order?.currentCurrencySelected?.currencyCode}
            </CurrencyChip>
            <b>{format.toAmountCentStr(order[FX_CALCULATOR_FORM_BUY_NAME])}</b>
          </CurrencyCodeBuyAmount>
          <CurrencyNameText>
            {
              CURRENCY_NAME_REFERENCE[
                order?.currentCurrencySelected?.currencyCode
              ]
            }
          </CurrencyNameText>
        </GridItem>
        <GridItem isMakePayment={isMakePayment}>
          <PayAmountText>
            {`AUD ${format.toAmountCentStr(
              order[FX_CALCULATOR_FORM_PAY_NAME]
            )}`}
          </PayAmountText>
          <RateText>{`(1 AUD = ${format.toAmountCentStr(
            order?.sellRate?.rate
          )})`}</RateText>
        </GridItem>
        {!isMakePayment && (
          <FullWidthRow>
            <DenominationSelector
              currencyCode={order.currentCurrencySelected.currencyCode}
              selectedDenomination={
                denominations[order.currentCurrencySelected.currencyCode]
              }
              handleChange={addDenomination}
            />
          </FullWidthRow>
        )}
      </CurrencyItemWrap>
    );
  };
  const renderCurrencyOrders = () => {
    if (isEmpty(currencyOrders)) return null;
    return (
      <SectionWrap>
        <FieldWrap>
          <FieldLabel>{t('fx_txn_details_order')}</FieldLabel>
          <FieldValue></FieldValue>
        </FieldWrap>
        <CurrencyOrdersWrap>
          {currencyOrders?.map((order, index) => (
            <Fragment key={index}>{renderCurrencyItem(order)}</Fragment>
          ))}
        </CurrencyOrdersWrap>
      </SectionWrap>
    );
  };

  const renderCustomerName = () => {
    if (isEmpty(customer)) return null;
    const customerFullName = `${customer?.firstName} ${customer?.lastName}`;
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('label_customer_name')}</FieldLabel>
        <FieldValue style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}>
          {customerFullName}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderCreatedDate = () => {
    if (isEmpty(createdDate)) return null;
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('label_created_date')}</FieldLabel>
        <FieldValue style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}>
          {createdDate}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderPaymentMethod = () => {
    if (isEmpty(paymentMethod)) return null;
    const paymentMethodTextReference = {
      [METHOD_DEPOSIT]: t(PAYMENT_METHOD_TEXT_REFERENCE[paymentMethod]),
      [METHOD_PAYID]: t(PAYMENT_METHOD_TEXT_REFERENCE[paymentMethod]),
      [METHOD_EPAYMENT]: `VISA - ${t('label_visa_end_with')} ${
        creditCardNo?.split('*')?.slice(-1)[0]
      }`,
    };
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('label_payment_method')}</FieldLabel>
        <FieldValue
          style={{
            padding: 0,
            marginTop: '4px',
            textAlign: 'left',
            textTransform: 'uppercase',
          }}
        >
          {paymentMethodTextReference[paymentMethod]}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderPickupLocation = () => {
    if (isEmpty(pickupLocationSelected)) return null;
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('fx_add_transaction_pickup_location')}</FieldLabel>
        <FieldValue style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}>
          {getPickupLocationName(pickupLocationSelected, pickupLocationType)}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderPickupDate = () => {
    if (isEmpty(pickupDateSelected)) return null;
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('fx_add_transaction_pickup_date_details')}</FieldLabel>
        <FieldValue style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}>
          {pickupDateSelected}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderDenomination = () => {
    if (isEmpty(orderNote)) return null;
    const notes = noteParts.join('.').trim();
    const formattedNote = formatOrderNote(denomination);
    return (
      <FieldWrap
        styles={{ padding: '10px 0' }}
        style={{
          display: 'grid',
          marginBottom: 0,
          borderBottom: !isEmpty(notes) && '1px solid var(--ds-bg-2)',
        }}
      >
        <FieldLabel>{t('label_denomination')}</FieldLabel>
        <FieldValue style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}>
          {formattedNote.join('/ ')}
        </FieldValue>
      </FieldWrap>
    );
  };

  const renderOrderNote = () => {
    const notes = noteParts.join('.').trim();

    if (isEmpty(notes)) return null;
    return (
      <>
        <FieldWrap
          styles={{ padding: '10px 0' }}
          style={{
            display: 'grid',
            marginBottom: 0,
          }}
        >
          <FieldLabel>{t('fx_txn_details_order_note')}</FieldLabel>
          <FieldValue
            style={{ padding: 0, marginTop: '4px', textAlign: 'left' }}
          >
            {notes}
          </FieldValue>
        </FieldWrap>
      </>
    );
  };

  const renderActionsButton = () => {
    if (isReceipt)
      return (
        <ActionWrapper>
          <ButtonCommon
            value={t('button_return_foreign_exchange')}
            color="var(--c-primary)"
            background="var(--bg-primary)"
            isFill={true}
            styles={{
              paddingInline: isDesktop ? '16px' : 0,
              width: isDesktop ? 'fit-content' : '100%',
            }}
            buttonIconStyles={{ paddingInline: 0 }}
            onClick={onClose}
            isUseKeyDown
          />
        </ActionWrapper>
      );
    return (
      <ActionWrapper>
        <ButtonCommon
          value={t('button_close')}
          color="var(--c-primary)"
          background="var(--bg-primary)"
          isFill={true}
          styles={{
            paddingInline: 0,
            width: isDesktop ? '50%' : '100%',
          }}
          buttonIconStyles={{ paddingInline: 0 }}
          onClick={onClose}
          isUseKeyDown
        />
      </ActionWrapper>
    );
  };

  if (isReceipt)
    return (
      <ReceiptDetails
        header={
          <ModalHeader $isReceipt={isReceipt}>{renderHeader()}</ModalHeader>
        }
        body={
          <ModalBody $isReceipt={isReceipt}>
            {renderCurrencyOrders()}
            {renderCustomerName()}
            {renderCreatedDate()}
            {renderPaymentMethod()}
            {renderPickupLocation()}
            {renderPickupDate()}
            {renderDenomination()}
            {renderOrderNote()}
          </ModalBody>
        }
        footer={
          <ModalFooter $isReceipt={isReceipt}>
            {renderActionsButton()}
          </ModalFooter>
        }
      />
    );
  return (
    <>
      <style>
        {`
          @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${
          TABLET_MAX_WIDTH - 1
        }px) {
            .modal-backdrop {
              display: none;
            }

            .transaction-details-preview-modal-backdrop {
              z-index: 1000;
              background: #212121;
              opacity: 0.7!important;
              display: initial!important;
            }
          }
        `}
      </style>
      <ModalStyled
        show={isOpen}
        onHide={() => {}}
        aria-labelledby="contained-modal-title-vcenter"
        className={FORM_TRANSACTION_DETAILS_MODAL}
        backdropClassName="transaction-details-preview-modal-backdrop"
        centered
      >
        <ModalHeader>{renderHeader()}</ModalHeader>
        <ModalBody>
          {renderCurrencyOrders()}
          {renderPickupLocation()}
          {renderPickupDate()}
          {renderDenomination()}
          {renderOrderNote()}
        </ModalBody>
        <ModalFooter>{renderActionsButton()}</ModalFooter>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    &::-webkit-scrollbar {
      width: 0px;
    }
    &::-webkit-scrollbar-thumb {
      background: transparent;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100vh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0px;
        padding-bottom: 100px;
        border-radius: 0px;
        box-shadow: none;
        background: var(--ds-c-white);
        width: 100%;
        height: fit-content;
        margin-inline: auto;
        border: none;
        border-radius: 8px 8px 0 0;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      border: none;
    }
    & .modal-content {
      padding: 16px;
      border-radius: 12px;
      box-shadow: var(--ds-bs-4);
      background: var(--ds-c-white);
      width: 375px;
      height: fit-content;
      margin-inline: auto;
      border: none;
    }
  }
`;

const ModalHeader = styled(Modal.Header)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    padding: 0;
    margin-inline: ${(props) => (props.$isReceipt ? '0px' : '16px')};
    margin-bottom: 0;
    height: ${(props) => (props.$isReceipt ? 'fit-content' : '44px')};
    border: none;
  }
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding: 0;
    margin-bottom: 0;
    margin-inline: 0px;
    display: block;
    padding-bottom: 16px;
    border-bottom: none;
  }
`;
const HeaderText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: var(--ds-c-green-default);

  margin: 0;
  padding: 0;
`;

const ModalBody = styled(Modal.Body)`
  background: var(--ds-c-white);
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    padding: 0;
    padding-inline: ${(props) => (props.$isReceipt ? '0px' : '16px')};
    padding-bottom: 122px;
  }
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding: 0;
    margin-bottom: 16px;
  }
`;
const FieldWrap = styled.div`
  display: flex;
  align-items: ${(props) => props.styles?.alignItems || 'center'};
  justify-content: start;
  height: fit-content;
  padding: ${(props) => props.styles?.padding};
  margin-bottom: 4px;
`;
const FieldLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: var(--ds-c-blue-hover);

  margin: 0;
  padding: ${(props) => props.styles?.padding || '0'};
  min-width: 150px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 18px;
  }
`;
const FieldValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  text-align: right;
  color: var(--ds-c-black);

  width: 100%;
  margin: 0;
  padding: 0;
  padding-left: 8px;

  & span {
    color: var(--ds-c-grey-neutral);
  }

  & span.rate {
    color: var(--ds-c-blue);
  }

  & span.credit-card {
    text-transform: initial;
    color: var(--ds-c-grey-dark);
  }

  & span.payment-method-txt {
    color: var(--ff-primary);
    line-height: 25px;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const CurrencyOrdersWrap = styled.div`
  width: 100%;
  height: fit-content;
`;
const CurrencyItemWrap = styled.div`
  width: 100%;
  min-height: ${(props) => (props.isMakePayment ? '60px' : '95px')};
  height: ${(props) => (props.isMakePayment ? '60px' : 'fit-content')};
  border-bottom: 1px solid var(--ds-bg-2);
  display: grid;
  grid-template-columns: 30px calc(60% - 30px) calc(40% - 30px);
  grid-gap: ${(props) => (props.isMakePayment ? '12px' : '0 12px')};
  &:last-child {
    border-bottom: none;
  }
`;
const GridItem = styled.div`
  width: 100%;
  display: grid;
  align-items: center;
  padding-block: 8px;
  padding-bottom: ${(props) => (props.isMakePayment ? '8px' : '0')};
  margin-bottom: 0;

  &:nth-child(1) {
    justify-content: start;
    align-items: center;
  }
  &:nth-child(2) {
  }
  &:last-child {
    justify-content: end;
    align-items: start;
  }
`;
const CurrencyNameText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  color: var(--ds-c-grey-dark);
  text-align: left;

  margin: 0;
  padding: 0;
`;
const CurrencyCodeBuyAmount = styled.div`
  ${paragraphMixin};
  font-weight: 500;

  display: flex;
  gap: 4px;
`;
const PayAmountText = styled.p`
  ${paragraphMixin};
  text-align: right;
`;
const RateText = styled.p`
  ${paragraphMixin};
  font-size: 12px;
  line-height: 15px;
  text-align: right;
  font-weight: 500;
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 44px;
  width: 100%;
  background: var(--ds-c-white);
  padding-inline: 0px;
  margin: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: 44px;
    border-top: unset;
    padding-inline: unset;
    margin: 0px;
  }
`;
const ModalFooter = styled(Modal.Footer)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: 16px;
    margin-inline: ${(props) => props.$isReceipt && '-16px'};
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #eef2f5;
    background: var(--ds-c-white);
    border-radius: 0px;
    border-top: 1px solid var(--ds-bg-2);
  }
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    border: none;
    padding: 0;
    display: flex;
    justify-content: space-between;
  }
`;

const CurrencyChip = styled.span`
  width: fit-content;
  height: 20px;
  padding-inline: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--ds-c-blue-dark);
  border-radius: 100px;

  font-family: var(--ff-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 15px;
  color: #fff;
`;
const FlagIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: cover;
  border: 1px solid var(--ds-bg-2);
  border-radius: 50%;
`;
const FullWidthRow = styled.div`
  width: 100%;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0px;
  padding: 0 0 0 42px;
`;

const SectionWrap = styled.div`
  background: #fbfbfb;
  margin-inline: -16px;
  padding-inline: 16px;
  padding-top: 8px;
  border-radius: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    border-radius: 8px;
  }
`;

const HeaderReceipt = styled.div`
  display: grid;
  justify-items: center;
  gap: 2px;
  width: 100%;
  margin-bottom: 10px;

  & img {
    width: 100px;
    height: 100px;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 0px;

    & img {
      width: 150px;
      height: 150px;
    }
  }
`;
const HeaderReceiptOrderRefText = styled.p`
  ${paragraphMixin};
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  text-transform: uppercase;
  color: var(--ds-c-blue);
`;
const HeaderReceiptOrderSuccessText = styled.p`
  ${paragraphMixin};
  font-weight: 600;
  font-size: 24px;
  line-height: 30px;
  color: var(--ds-c-green-default);
`;

export default FormTransactionDetails;
